import React, { useState } from 'react';
import { useAppStateApartmentCompareListStore } from '../../../../common/store/AppStateStore.zustand';
import { useApartmentCompareAPI } from '../../api/ApartmentCompare.api';
import { ApartmentCompare } from '../../type/ApartmentCompare.type';
import AddApartmentModal, { AddApartmentModalData } from './AddApartmentModal';
import EditApartmentModal, { EditApartmentModalData } from './EditApartmentModal';

export interface ManageApartmentModalData {
  isOpen: boolean;
  onClose: () => void;
}

const ManageApartmentModal = ({ isOpen, onClose }: ManageApartmentModalData) => {
  const { deleteApartmentCompare, getApartmentCompareList } = useApartmentCompareAPI();
  const { apartmentCompareList, setApartmentCompareList } = useAppStateApartmentCompareListStore();
  const [addModalData, setAddModalData] = useState<AddApartmentModalData>();
  const [editModalData, setEditModalData] = useState<EditApartmentModalData>();

  const handleAdd = () => {
    setAddModalData({ isOpen: true, onClose: () => setAddModalData(undefined) });
  };

  const handleEdit = (apt: ApartmentCompare) => {
    setEditModalData({
      isOpen: true,
      apartmentCompare: apt,
      onClose: () => setEditModalData(undefined),
    });
  };

  const handleDelete = async (id: string) => {
    await deleteApartmentCompare(id);
    const { data } = await getApartmentCompareList();
    setApartmentCompareList(data);
  };

  if (!isOpen) return <></>;

  return (
    <div className="fixed h-screen inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">아파트 비교 관리</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          <div className="mb-6 flex justify-between items-center">
            <p className="text-gray-600">총 {apartmentCompareList.length}개의 아파트가 등록되어 있습니다.</p>
            <button
              onClick={handleAdd}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm flex items-center !rounded-button"
            >
              <i className="fas fa-plus mr-2"></i> 아파트 추가
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg">
            {apartmentCompareList.length === 0 && (
              <div className="p-8 text-center text-gray-400">
                <i className="fas fa-building text-3xl mb-3 block"></i>
                등록된 아파트가 없습니다.
              </div>
            )}
            {apartmentCompareList.map((apt) => (
              <div key={apt.id} className="p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-100">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-orange-50 rounded-full flex items-center justify-center mr-3">
                      <i className="fas fa-building text-orange-300"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800">{apt.name}</h3>
                      <p className="text-sm text-gray-500">
                        {apt.sidoName} {apt.sigunguName} · {apt.aptName} {apt.excluUseAr}㎡
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      className="text-gray-400 hover:text-blue-500 p-2"
                      onClick={() => handleEdit(apt)}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="text-gray-400 hover:text-red-500 p-2"
                      onClick={() => handleDelete(apt.id)}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm !rounded-button"
          >
            닫기
          </button>
        </div>
      </div>
      {addModalData && <AddApartmentModal {...addModalData} />}
      {editModalData && <EditApartmentModal {...editModalData} />}
    </div>
  );
};

export default ManageApartmentModal;
